import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useCurrentUser } from "Auth/Hooks/useCurrentUser";
import {
  CurrencyCodes,
  type ExchangeRateDto,
  patchUsersSettings,
} from "Infrastructure/Api/Api";
import {
  getSavedCurrency,
  saveCurrencyPreference,
} from "Infrastructure/Utils/CurrencyPreferenceUtils";
import { useExchangeRatesQuery } from "Intl/Api/Queries/useExchangeRatesQuery";

export interface CurrencyContextValue {
  userCurrency: CurrencyCodes;
  setCurrency: (currency: CurrencyCodes) => void;
  getRate: (currency: CurrencyCodes) => ExchangeRateDto | undefined;
  isLoading: boolean;
}

export const CurrencyContext = createContext<CurrencyContextValue>({
  userCurrency: CurrencyCodes.EUR,
  setCurrency: () => {},
  getRate: () => undefined,
  isLoading: false,
});

export const CurrencyProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactNode => {
  const { user } = useCurrentUser();

  // Local state — source of truth for immediate UI updates
  const [userCurrency, setUserCurrency] = useState<CurrencyCodes>(
    () => user?.currency ?? getSavedCurrency(),
  );

  // Sync from server when user info loads (e.g. after login)
  useEffect(() => {
    if (user?.currency) {
      setUserCurrency(user.currency);
    }
  }, [user?.currency]);

  const isNonEur = userCurrency !== CurrencyCodes.EUR;
  const { data: rates, isLoading } = useExchangeRatesQuery(isNonEur);

  const setCurrency = useCallback((currency: CurrencyCodes) => {
    setUserCurrency(currency);
    saveCurrencyPreference(currency);
    // Fire-and-forget API sync
    patchUsersSettings({ currency }).catch(() => {});
  }, []);

  const rateMap = useMemo(() => {
    const map = new Map<CurrencyCodes, ExchangeRateDto>();
    if (rates) {
      for (const rate of rates) {
        map.set(rate.currency, rate);
      }
    }
    return map;
  }, [rates]);

  const value = useMemo<CurrencyContextValue>(
    () => ({
      userCurrency,
      setCurrency,
      getRate: (currency: CurrencyCodes) => rateMap.get(currency),
      isLoading,
    }),
    [userCurrency, setCurrency, rateMap, isLoading],
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
